import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import {
    NgbDatepickerI18n,
    NgbDatepickerModule,
    NgbDateStruct,
    NgbDropdownModule
} from '@ng-bootstrap/ng-bootstrap';
import { QueueStatusGroup } from '../../synchronization.model';
import {
    CountsArray,
    MonitoringCountsMap,
    QueueQueryFilters,
    QueueTimeWindow,
    ROLLING_WINDOWS
} from '../queue-request.model';
import { dayPickerRange, toDayKey } from '../queue-request.util';
import { DanishDatepickerI18n, toDateStruct } from '../danish-datepicker-i18n';

const EMPTY_COUNT = '\u00A0'; // empty char that still takes space.
/** Empty, not 0: before the counts land there is no number to show, and 0 would be a claim. */
function label(counts: CountsArray | null | undefined): string {
    return counts ? String(counts[0]) : EMPTY_COUNT;
}

/**
 * Search, time window and status group.
 *
 * One whole-object model rather than a signal per control, chiefly for atomicity: picking a date has
 * to set `window: 'day'` and `day` in a single write, since the intermediate
 * `{ window: 'day', day: null }` is a valid server query meaning "everything, no window".
 *
 * The badges are tiered — each reads the filters above it and nothing at or below:
 *   time badge for W  = counts[W][0]        (unaffected by the selected window or status)
 *   status badge      = counts[selectedWindow][group]
 * Both come from one payload that the parent fetched with scope + template + search, so switching
 * window or status re-reads rather than re-requests. When the payload is not there yet the badges
 * render empty — 0 would be a claim, empty is an admission.
 */
@Component({
    selector: 'app-queue-request-filters',
    templateUrl: './queue-request-filters.component.html',
    styleUrl: './queue-request-filters.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgbDatepickerModule, NgbDropdownModule],
    providers: [{ provide: NgbDatepickerI18n, useClass: DanishDatepickerI18n }]
})
export class QueueRequestFiltersComponent {
    readonly query = model.required<QueueQueryFilters>();

    /** Null while the parent does not know yet — every badge then renders empty. */
    readonly counts = input<MonitoringCountsMap | null>(null);

    readonly timeWindows = ROLLING_WINDOWS;

    readonly statusGroups: readonly { key: QueueStatusGroup; label: string; btnClass: string }[] = [
        { key: 'all', label: 'Alle', btnClass: 'primary' },
        { key: 'running', label: 'Kørende', btnClass: 'primary' },
        { key: 'finished', label: 'Færdige', btnClass: 'success' },
        { key: 'warning', label: 'Advarsler', btnClass: 'warning' },
        { key: 'failed', label: 'Fejlede', btnClass: 'danger' }
    ];

    /** The picker is capped to the same 7 days the widest rolling window covers. */
    private readonly pickerRange = dayPickerRange();
    readonly minPickableDay = toDateStruct(this.pickerRange.min);
    readonly maxPickableDay = toDateStruct(this.pickerRange.max);

    /**
     * Two months whenever the 7 days straddle a month boundary. The datepicker renders no navigation
     * here, so a day in the earlier month would otherwise sit on a page the user cannot reach.
     */
    readonly pickerMonths = this.pickerRange.min.getMonth() === this.pickerRange.max.getMonth() ? 1 : 2;

    /**
     * Total kørsler per window — the number on each time button.
     *
     * Independent of the selected window and of the status group, by the tiering rule: those controls
     * sit at or below this one. Empty string, never 0, when the counts are not in yet.
     */
    protected readonly windowCounts = computed(() => {
        const map = this.counts();
        return {
            '7d': label(map?.sevenDays),
            '24h': label(map?.twentyFourHours),
            '1h': label(map?.oneHour)
        } as Record<Exclude<QueueTimeWindow, 'day'>, string>;
    });

    /**
     * '*' rather than 0 before a date is picked — there is no count to show yet, and a 0 would read
     * as "that day has no kørsler", which is a different and wrong claim. Empty while loading, which
     * is a third and different statement again.
     */
    protected readonly dayCountLabel = computed(() => {
        const map = this.counts();
        if (!map) return '*';
        return map.day ? String(map.day[0]) : '*';
    });

    /** The counts row the status badges read: the selected window, all five groups. */
    protected readonly statusCounts = computed<CountsArray | null>(() => {
        const map = this.counts();
        if (!map) return null;
        switch (this.query().window) {
            case '7d': return map.sevenDays;
            case '1h': return map.oneHour;
            case 'day': return map.day;
            default: return map.twentyFourHours;
        }
    });

    private readonly statusIndex: Record<QueueStatusGroup, number> = {
        all: 0, running: 1, finished: 2, warning: 3, failed: 4
    };

    countFor(group: QueueStatusGroup): string {
        const counts = this.statusCounts();
        return counts ? String(counts[this.statusIndex[group]]) : EMPTY_COUNT;
    }

    /** The 4th time button doubles as its own label once a date is picked. */
    protected readonly dayFilterLabel = computed(() => {
        const day = this.query().day;
        if (!day) return 'Vælg dag';
        const [, month, date] = day.split('-');
        return `${date}-${month}`;
    });

    onSearchChange(value: string): void {
        // Written straight through, so the box and the URL keep up with typing. The parent debounces
        // before this reaches the network.
        this.query.update(current => ({ ...current, search: value }));
    }

    clearSearch(): void {
        this.query.update(current => ({ ...current, search: '' }));
    }

    setWindow(window: QueueTimeWindow): void {
        this.query.update(current => ({ ...current, window }));
    }

    /**
     * Picking a date is what selects the day window — the button alone cannot, since a `day` filter
     * without a date would silently mean "everything".
     */
    pickDay(date: NgbDateStruct): void {
        const day = toDayKey(new Date(date.year, date.month - 1, date.day));
        this.query.update(current => ({ ...current, window: 'day', day }));
    }

    setStatusGroup(statusGroup: QueueStatusGroup): void {
        this.query.update(current => ({ ...current, statusGroup }));
    }
}
